import type {
  InspectionAssigneeCandidateResponse,
  InspectionRequest,
  InspectionResponse,
  SelectOption,
} from 'src/api/ProactApiClient';
import {InspectionStateEnum} from 'src/api/ProactApiClient';
import {useAppSelector} from 'src/redux/hooks';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';

export const InspectionDocumentTitle = 'Inspections';

// Edit/delete are only meaningful pre-work: Draft (unpublished) and
// Scheduled (published, not started).
export const isInspectionEditableState = (
  state: InspectionStateEnum
): boolean =>
  state === InspectionStateEnum.Draft ||
  state === InspectionStateEnum.Scheduled;

export const useIsInspectionActor = (
  inspection: Pick<InspectionResponse, 'assigneeId' | 'creatorId'> | undefined
): boolean => {
  const perms = useInspectionPermissions();
  const currentUserId = useAppSelector(state => state.session.user?.id);

  if (!inspection) return false;

  return (
    inspection.assigneeId === currentUserId ||
    inspection.creatorId === currentUserId ||
    perms.manage ||
    perms.manageAll
  );
};

export const useCanRecordInspectionDevices = (
  inspection:
    | Pick<InspectionResponse, 'assigneeId' | 'creatorId' | 'state'>
    | undefined
): boolean => {
  const perms = useInspectionPermissions();
  const isActor = useIsInspectionActor(inspection);

  return (
    !!inspection &&
    perms.update &&
    isActor &&
    inspection.state === InspectionStateEnum.InProgress
  );
};

export const InspectionCreateFormMeta = (
  sites: SelectOption[],
  inspectionTypes: SelectOption[],
  assigneeCandidates: InspectionAssigneeCandidateResponse[]
): IFormFieldV2[] => [
  {
    name: 'siteId',
    label: 'Site',
    type: 'select',
    options: sites as ISelectOption[],
    required: true,
  },
  {
    name: 'inspectionTypeId',
    label: 'Inspection Type',
    type: 'select',
    options: inspectionTypes as ISelectOption[],
    required: true,
  },
  {
    name: 'assigneeId',
    label: 'Assignee',
    type: 'select',
    options: assigneeCandidates.map(candidate => ({
      id: candidate.userId,
      label: `${candidate.name} (${candidate.openInspectionCount} open)`,
    })) as ISelectOption[],
    required: false,
    description: 'Leave blank to self-assign or assign later.',
  },
  {
    name: 'dueAt',
    label: 'Due Date',
    type: 'date',
    required: false,
  },
];

// Edit mode omits Assignee — assignment has its own drawer/inline flow.
export const InspectionEditFormMeta = (
  sites: SelectOption[],
  inspectionTypes: SelectOption[]
): IFormFieldV2[] => [
  {
    name: 'siteId',
    label: 'Site',
    type: 'select',
    options: sites as ISelectOption[],
    required: true,
  },
  {
    name: 'inspectionTypeId',
    label: 'Inspection Type',
    type: 'select',
    options: inspectionTypes as ISelectOption[],
    required: true,
  },
  {
    name: 'dueAt',
    label: 'Due Date',
    type: 'date',
    required: false,
  },
];

// The API sends explicit `null` where the generated TS types say `undefined`.
export const MapInspectionResponseToInspectionRequest = (
  data: InspectionResponse
): InspectionRequest => {
  return {
    id: data.id,
    // The 'select' field type stores the chosen option id as a string.
    siteId: Number(data.siteId),
    inspectionTypeId: Number(data.inspectionTypeId),
    assigneeId: data.assigneeId ? Number(data.assigneeId) : undefined,
    dueAt: data.dueAt ?? undefined,
  };
};
